import { IModuleConfiguration, ModuleConfiguration } from 'src/types/system_types';
import { IViewModuleConfiguration } from './view_module';
import { getActiveRoute } from 'src/core/settings';

import Micro, { IMicroConfiguration } from'../../../pip/library/pi-micro/pi-micro.js';
import navigation from'../../../pip/library/pi-navigation/pi-navigation.js';
import ess from'../../../pip/library/pi-ess/pi-ess.js';
import f1 from'../../../pip/library/pi-f1/pi-f1.js';

export interface INavigationMicroConfiguration extends IMicroConfiguration {
  _: never;
}

export interface INavigationModuleConfiguration extends IModuleConfiguration {
  hostname: string;
  configurations: {
    point_cut: string;
    routes: string;
  };
  /**
   * configure the default paths for resources and views
   */
  micro: INavigationMicroConfiguration;
  paths: {
    routes: {
      unauthenticated: string;
      authenticated: string;
      not_found: string;
    };
    patterns: {
      authenticated: string[];
    };
  };
}

class HostingMicro extends Micro<INavigationMicroConfiguration> {
  readonly id = 'hosting';
}

export class NavigationModule extends ModuleConfiguration {
  configuration: INavigationModuleConfiguration;
  micro: HostingMicro;

  initialize(configuration: INavigationModuleConfiguration) {
    return new Promise((resolve) => {
      const conf = (this.configuration = this.__defaultConfiguration(configuration));

      const pointCutPath = navigation.uri.write(conf.configurations.point_cut);
      const routesPath = navigation.uri.write(conf.configurations.routes);

      const pointCutJSON = pointCutPath ? fetch(pointCutPath).then((response) => response.json()) : f1.done(null);
      const routesJSON = routesPath ? fetch(routesPath).then((response) => response.json()) : f1.done(null);

      Promise.all([pointCutJSON, routesJSON])
        .then(([pointCutConfig, routesConfig]) => {
          conf.micro.routes = f1.read('/data', {}, routesConfig);
          conf.micro.view.point_cuts = f1.read('/data', [], pointCutConfig);
        })
        .then(() => resolve(conf));
    });
  }

  activate(configuration: { navigation: INavigationModuleConfiguration; view: IViewModuleConfiguration }) {
    return new Promise((resolve) => {
      const microConf = configuration.navigation.micro;

      microConf.view.constants = { ...(microConf.view.constants || {}), ...(configuration.view.constants || {}) };

      // the entire AHP layout system requires x-shell=owner on the body; we should change the styling rules

      const microContainerEl = document.createElement('main');
      const bodyEl = document.body;

      microContainerEl.setAttribute('id', 'micro-container');
      bodyEl.setAttribute('x-shell', 'owner');

      bodyEl.appendChild(microContainerEl);

      this.micro = new HostingMicro();
      this.micro.startup(microContainerEl, configuration.navigation.micro).then(() => {
        this.__authenticatedRouteRefresh();

        const activeRoute = getActiveRoute();
        if (!activeRoute) {
          this.__goToDefaultRoute();
        }

        resolve(this.configuration);
      });
    });
  }

  deactivate() {
    document.getElementById('micro-container')?.remove();
  }

  private __authenticatedRouteRefresh() {
    ess.on(
      {
        ns: 'ahp',
        action: 'changeState'
      },
      (data) => {
        const safeRoutes = f1.read('/paths/patterns/authenticated', [] as string[], this.configuration);
        const isAuthenticated = this.ahp.session.isAuthenticated;

        const hasSessionStatusChanged = data.site === 'session';
        if (hasSessionStatusChanged) {
          return this.__goToDefaultRoute();
        }

        const activeRoute = getActiveRoute();
        const hasNoActiveRoute = !activeRoute;
        if (hasNoActiveRoute) {
          return this.__goToDefaultRoute();
        }

        const hasSafeRoutes = safeRoutes.length > 0;
        const isInsideSafeRoute = safeRoutes.find((base) => activeRoute.indexOf(base) === 0) != null;

        const isPermissibleRoute = isAuthenticated || !hasSafeRoutes || !isInsideSafeRoute;
        if (!isPermissibleRoute) {
          return this.__goToDefaultRoute();
        }
      }
    );
  }

  private __goToDefaultRoute() {
    const defaultRoute = this.ahp.session.isAuthenticated
      ? f1.read('/paths/routes/authenticated', '', this.configuration)
      : f1.read('/paths/routes/unauthenticated', '', this.configuration);

    const isNewRoute = `/${defaultRoute}` !== getActiveRoute();
    if (isNewRoute) {
      const activeParams = f1.read('/1', null, location.href.split('?'));
      const embedParams = activeParams ? `?${activeParams}` : '';
      const newRoute = `#/${defaultRoute}${embedParams}`;
      // Clears URL of queryparams before queryparams are applied again.
      window.history.replaceState({}, document.title, window.location.pathname);
      navigation.goTo(newRoute);
    }
  }

  private __defaultConfiguration(configuration: INavigationModuleConfiguration) {
    return f1.merge(
      'over',
      {
        hostname: '@tenant.@environment.@product.eab.com',
        configurations: {
          point_cut: '@platform/point_cut.json',
          routes: '@platform/routes.json'
        },
        paths: {
          routes: {
            unauthenticated: '/auth/login',
            authenticated: '/my',
            not_found: '/404'
          },
          patterns: {
            authenticated: []
          }
        },
        micro: {
          view: {}
        }
      },
      configuration
    ) as INavigationModuleConfiguration;
  }
}
